/**
 * Page Catalogue SOFI — GS-Admin
 * Référence permanente des tables SOFI disponibles pour l'ETL.
 * Permet de trouver la bonne table pour toute nouvelle mesure.
 */

import { useMemo, useState } from 'react';
import { PageHeader } from '@gs/ui';
import { SOFI_CATALOG, type MedallionLayer, type SofiDomain } from '@gs/etl/sofiCatalog';

const DOMAIN_LABELS: Record<SofiDomain, string> = {
  projets: 'Projets & Plans',
  facturation: 'Facturation',
  flotte: 'Flotte',
  rh: 'RH & Temps',
  operations: 'Opérations',
  config: 'Configuration',
  archives: 'Archives',
  clients: 'Clients',
};

const DOMAIN_COLORS: Record<SofiDomain, string> = {
  projets: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  facturation: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  flotte: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  rh: 'bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  operations: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  config: 'bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  archives: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500',
  clients: 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
};

const MEDALLION_LABELS: Record<string, { label: string; color: string }> = {
  bronze: { label: 'Bronze', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  silver: { label: 'Silver', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  gold: { label: 'Gold', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200' },
  null: { label: 'Disponible', color: 'bg-slate-50 text-slate-400 dark:bg-slate-800 dark:text-slate-500' },
};

function rowBadge(rows: number) {
  if (rows > 500000) return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  if (rows > 50000) return 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300';
  if (rows > 1000) return 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  return 'bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400';
}

function fmtRows(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}

type LayerFilter = '' | MedallionLayer;

export default function CatalogSofiPage() {
  const [search, setSearch] = useState('');
  const [domainFilter, setDomain] = useState<SofiDomain | ''>('');
  const [layerFilter, setLayer] = useState<LayerFilter>('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return SOFI_CATALOG.filter((t) => {
      if (domainFilter && t.domain !== domainFilter) return false;
      if (layerFilter !== '' && t.medallion !== (layerFilter || null)) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.keyColumns.some((c) => c.toLowerCase().includes(q))
      );
    });
  }, [search, domainFilter, layerFilter]);

  const totalRows = filtered.reduce((s, t) => s + t.rows, 0);

  const layerSelectValue =
    layerFilter === '' ? '' : layerFilter === null ? 'null' : layerFilter;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Catalogue SOFI"
        description="586 tables — trouvez la source pour toute nouvelle mesure"
      />

      <div className="flex flex-wrap gap-3">
        <input
          type="search"
          placeholder="Chercher table, colonne, concept..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="touch-manipulation flex-1 min-w-[200px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <select
          value={domainFilter}
          onChange={(e) => setDomain(e.target.value as SofiDomain | '')}
          className="touch-manipulation rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-300"
        >
          <option value="">Tous les domaines</option>
          {Object.entries(DOMAIN_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={layerSelectValue}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') setLayer('');
            else if (v === 'null') setLayer(null);
            else setLayer(v as 'bronze' | 'silver' | 'gold');
          }}
          className="touch-manipulation rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-300"
        >
          <option value="">Toutes les couches</option>
          <option value="bronze">Bronze (staging)</option>
          <option value="silver">Silver (curated)</option>
          <option value="gold">Gold (metrics)</option>
          <option value="null">Disponible (non intégré)</option>
        </select>
      </div>

      <div className="flex gap-4 text-sm text-slate-500 dark:text-slate-400">
        <span>
          <strong className="text-slate-700 dark:text-slate-200">{filtered.length}</strong> tables
        </span>
        <span>
          <strong className="text-slate-700 dark:text-slate-200">{fmtRows(totalRows)}</strong> lignes
        </span>
        <span>
          <strong className="text-green-600">{filtered.filter((t) => t.medallion).length}</strong> dans le
          pipeline
        </span>
      </div>

      <div className="space-y-2">
        {filtered.map((t) => {
          const med = MEDALLION_LABELS[String(t.medallion)];
          const open = expanded === t.name;

          return (
            <div
              key={t.name}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setExpanded(open ? null : t.name);
                }
              }}
              className={`rounded-xl border transition-colors cursor-pointer touch-manipulation
                ${
                  open
                    ? 'border-brand-300 dark:border-brand-600 bg-white dark:bg-slate-800'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
              onClick={() => setExpanded(open ? null : t.name)}
            >
              <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
                <code className="text-sm font-medium text-slate-900 dark:text-slate-100">{t.name}</code>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${rowBadge(t.rows)}`}>
                  {fmtRows(t.rows)}
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${DOMAIN_COLORS[t.domain]}`}>
                  {DOMAIN_LABELS[t.domain]}
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${med.color}`}>{med.label}</span>
                <span className="ml-auto text-xs text-slate-400">{t.cols} colonnes</span>
              </div>

              <div className="px-4 pb-2">
                <p className="text-sm text-slate-500 dark:text-slate-400">{t.description}</p>
              </div>

              {open && (
                <div className="px-4 pb-4 space-y-3 border-t border-slate-100 dark:border-slate-700 pt-3">
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                      Colonnes clés
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {t.keyColumns.map((c) => (
                        <code
                          key={c}
                          className="text-xs bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded"
                        >
                          {c}
                        </code>
                      ))}
                    </div>
                  </div>

                  {t.fkRefs.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                        Relations
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {t.fkRefs.map((r) => (
                          <span key={r} className="text-xs text-blue-600 dark:text-blue-400">
                            ↗ {r}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {t.stagingTable && (
                    <div>
                      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                        Table Bronze
                      </p>
                      <code className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded">
                        {t.stagingTable}
                      </code>
                    </div>
                  )}

                  {t.notes && (
                    <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded px-3 py-2">
                      Note : {t.notes}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400 text-sm">
            Aucune table trouvée pour cette recherche.
          </div>
        )}
      </div>
    </div>
  );
}
